// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from '@codex/basics';
import styled, { css } from 'styled-components';
import { CustomIcon } from '../../Icon';

const Label = styled.label`
  line-height: 16px;
  cursor: pointer;
  font-size: 12px;
  display: ${({ theme }) => theme.name === 'brightworld' && 'none'};
  margin: 5px 0 0;

  @media ${({ theme }) => theme.breakpoints.tablet} {
    display: block;
    margin: 0 0 0 5px;
  }

  @media ${({ theme }) => theme.breakpoints.desktop} {
    display: block;
    font-size: 13px;
    margin: 3px 0 0;
  }
`;

const Counter = styled.span`
  position: absolute;
  top: 4px;
  left: 7px;
  padding: 0 3px;
  height: 17px;
  border-radius: 8px;
  vertical-align: middle;
  text-align: center;
  line-height: 17px;
  color: ${({ theme }) => theme.colors.shades[7]};
  background-color: ${({ color }) => color};

  ${({ count }) => count < 10 &&
    css`
      padding: 0;
      width: 17px;
      border-radius: 50%;
    `};

  @media ${({ theme }) => theme.breakpoints.mobileMin} {
    left: 4px;
  }

  @media ${({ theme }) => theme.breakpoints.desktop} {
    left: 10px;
  }
`;

const LinkItemWrapper = styled.div`
  position: relative;
  height: 100%;
`;

const LinkItemLink = styled(Link)`
  position: relative;
  flex: 1;
  display: inline-flex;
  flex-direction: column;
  margin: -8px -12px -5px -12px;
  padding: 8px 12px 5px 12px;
  align-items: center;
  border: 1px solid transparent;
  color: ${({ theme }) => theme.colors.shades[7]};

  ${props => props.openSubMenu &&
    css`
      z-index: 1001;
      border-color: ${props.theme.colors.shades[5]};
      border-bottom-color: transparent;
      background-color: ${props.theme.colors.shades[7]};
    `};

  ${props => props.openSubMenu &&
    css`
      color: ${({ theme }) => theme.colors.link.default};

      &:hover {
        color: ${({ theme }) => theme.colors.link.default};
      }
    `};

  @media ${({ theme }) => theme.breakpoints.mobileMin} {
    flex-direction: row;
    margin-left: -18px;
    padding-left: 18px;
  }

  @media ${({ theme }) => theme.breakpoints.desktop} {
    flex-direction: column;
    margin-left: -12px;
    padding-left: 12px;
    color: ${({ theme }) => (theme.name === 'brightworld'
    ? theme.colors.link.default
    : theme.colors.shades[0])};
  }
`;

const LinkItemIcon = styled(CustomIcon)`
  position: relative;
  height: ${({ theme }) => (theme.name === 'brightworld' ? '25px' : '20px')};
  width: ${({ theme }) => (theme.name === 'brightworld' ? '25px' : '20px')};
  fill: currentColor;
  stroke: transparent;

  > svg {
    height: 100%;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }
`;

export const LinkItem = React.forwardRef((props, ref) => (
  <LinkItemWrapper
    onClick={props.onClick}
    onMouseEnter={props.onMouseEnter}
    onMouseLeave={props.onMouseLeave}
    className={props.className}
  >
    <LinkItemLink
      href={props.href}
      ref={ref}
      onClick={props.onClick}
      data-interaction={props.interaction}
      openSubMenu={props.openSubMenu}
    >
      <LinkItemIcon
        icon={props.icon}
        iconCDNDesktop={props.iconCDNDesktop}
        iconCDNMobile={props.iconCDNMobile}
      />
      {(props.badge.count > 0 || props.badge.displayZero) && (
        <Counter color={props.badge.color} count={props.badge.count}>
          {props.badge.shortenMultipleDigitNumbers && props.badge.count >= 10
            ? '9+'
            : props.badge.count}
        </Counter>
      )}
      <Label>{props.label}</Label>
    </LinkItemLink>
  </LinkItemWrapper>
));

LinkItem.propTypes = {
  label: PropTypes.string.isRequired,
  href: PropTypes.string,
  className: PropTypes.string,
  icon: PropTypes.string.isRequired,
  iconCDNDesktop: PropTypes.string,
  iconCDNMobile: PropTypes.string,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  badge: PropTypes.shape({
    contentElementId: PropTypes.string,
    color: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    count: PropTypes.number,
    /**
     * Force badge to be displayed even if `badge.count` is zero
     */
    displayZero: PropTypes.bool,
    shortenMultipleDigitNumbers: PropTypes.bool
  }),
  interaction: PropTypes.string,
  openSubMenu: PropTypes.bool
};

LinkItem.defaultProps = {
  className: '',
  iconCDNDesktop: '',
  iconCDNMobile: '',
  onClick: undefined,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  href: undefined,
  badge: {
    contentElementId: undefined,
    color: 'transparent',
    count: 0,
    displayZero: false,
    shortenMultipleDigitNumbers: false
  },
  interaction: '',
  openSubMenu: false
};

export const HeaderMenuSpacer = styled.div`
  flex: 1;
`;
